import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, CheckCircle, Users, Clock, MessageSquare, Shield, Zap, ArrowRight, Star, Target, FileText, Video } from "lucide-react";
import { useState } from "react";
import { Link } from "wouter";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import ProjectForm from "@/components/ProjectForm";

export default function HowItWorks() {
  const [showProjectForm, setShowProjectForm] = useState(false);

  const openProjectForm = () => setShowProjectForm(true);
  const closeProjectForm = () => setShowProjectForm(false);

  const howItWorksSteps = [
    {
      step: "01",
      title: "Discovery Call",
      duration: "30 minutes",
      description: "We understand your project requirements, technical stack, team culture, and timeline.",
      icon: <MessageSquare className="w-8 h-8" />,
      details: [
        "Project scope and technical requirements analysis",
        "Team structure and culture assessment",
        "Timeline and budget discussion",
        "Success metrics definition"
      ],
      color: "from-cyber-purple to-neon-pink"
    },
    {
      step: "02", 
      title: "Talent Matching",
      duration: "24 hours",
      description: "Our AI-powered system matches you with pre-vetted South African developers from our talent pool.",
      icon: <Target className="w-8 h-8" />,
      details: [
        "AI-powered skill matching algorithm",
        "Cultural fit assessment",
        "Timezone and availability alignment",
        "Portfolio and experience review"
      ],
      color: "from-neon-pink to-cyber-gold"
    },
    {
      step: "03",
      title: "Developer Presentation",
      duration: "24 hours", 
      description: "Receive detailed profiles of 2-3 perfectly matched developers with their portfolios and experience.",
      icon: <FileText className="w-8 h-8" />,
      details: [
        "Comprehensive developer profiles",
        "Code samples and portfolio review",
        "Previous project case studies",
        "Technical assessment results"
      ],
      color: "from-cyber-gold to-electric-blue"
    },
    {
      step: "04",
      title: "Interview Process",
      duration: "Flexible",
      description: "Conduct interviews with your shortlisted candidates via video calls or technical assessments.",
      icon: <Video className="w-8 h-8" />,
      details: [
        "Structured technical interviews",
        "Culture fit conversations",
        "Live coding sessions (optional)",
        "Reference checks and verification"
      ],
      color: "from-electric-blue to-cyber-purple"
    },
    {
      step: "05",
      title: "Onboarding",
      duration: "7 days",
      description: "Your chosen developer starts working with full support and continuous monitoring.",
      icon: <Zap className="w-8 h-8" />,
      details: [
        "Contract setup and legal documentation",
        "Development environment setup",
        "Project kickoff and goal alignment",
        "Ongoing support and monitoring"
      ],
      color: "from-cyber-purple to-neon-pink"
    }
  ];

  const guarantees = [
    {
      title: "7-Day Guarantee",
      description: "Find your perfect developer within 7 days or we'll keep searching for free",
      icon: <Clock className="w-6 h-6" />
    },
    {
      title: "Risk-Free Trial",
      description: "30-day money-back guarantee if you're not completely satisfied",
      icon: <Shield className="w-6 h-6" />
    },
    {
      title: "Pre-Vetted Talent",
      description: "All developers pass our rigorous technical and soft skills assessment",
      icon: <CheckCircle className="w-6 h-6" />
    },
    {
      title: "Ongoing Support",
      description: "Dedicated account manager and 24/7 support throughout the engagement",
      icon: <Users className="w-6 h-6" />
    }
  ];

  return (
    <div className="min-h-screen bg-dark-slate text-white overflow-x-hidden">
      {/* Navigation */}
      <Navigation onBookCall={openProjectForm} />

      {/* Hero Section */}
      <section className="flex items-center relative overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 z-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-cyber-purple rounded-full mix-blend-multiply filter blur-xl opacity-50 animate-drift"></div>
          <div className="absolute top-40 right-10 w-72 h-72 bg-cyber-blue rounded-full mix-blend-multiply filter blur-xl opacity-50 animate-orbit" style={{animationDelay: '2s'}}></div>
          <div className="absolute bottom-20 left-1/2 w-72 h-72 bg-neon-pink rounded-full mix-blend-multiply filter blur-xl opacity-50 animate-spiral" style={{animationDelay: '4s'}}></div>
        </div>
        <div className="container mx-auto px-6 py-16 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-7xl font-black mb-2 text-shadow mt-12">
              How It Works
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-4 leading-relaxed max-w-3xl mx-auto">
              From discovery to deployment in just 7 days. Our streamlined process ensures you get the perfect South African developer for your FinTech startup.
            </p>
            <p className="text-xl text-gray-300 mb-6 max-w-2xl mx-auto">
              Our battle-tested 5-step process gets you from requirement to onboarding in record time
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-1 sm:space-y-0 sm:space-x-8 text-gray-400">
              <div className="flex items-center">
                <Clock className="w-5 h-5 mr-2 text-cyber-purple" />
                <span>7 Day Guarantee</span>
              </div>
              <div className="flex items-center">
                <Shield className="w-5 h-5 mr-2 text-neon-pink" />
                <span>Risk-Free Trial</span>
              </div>
              <div className="flex items-center">
                <Star className="w-5 h-5 mr-2 text-cyber-gold" />
                <span>Pre-Vetted Talent</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Steps */}
      <section className="py-8 bg-dark-slate">
        <div className="container mx-auto px-6">
          <div className="text-center mb-6">
            <h2 className="text-4xl font-bold">
              <span className="bg-gradient-to-r from-cyber-purple to-neon-pink bg-clip-text text-transparent">
                How It Works
              </span>
            </h2>
          </div>

          <div className="space-y-8">
            {howItWorksSteps.map((step, index) => (
              <div key={index} className="relative">
                {/* Connection Line */}
                {index < howItWorksSteps.length - 1 && (
                  <div className="absolute left-8 top-20 w-0.5 h-24 bg-gradient-to-b from-gray-600 to-transparent"></div>
                )}
                
                <div className="flex flex-col lg:flex-row items-start lg:items-center gap-8">
                  {/* Step Number & Icon */}
                  <div className="flex-shrink-0">
                    <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${step.color} flex items-center justify-center text-white font-bold text-xl shadow-lg`}>
                      {step.step}
                    </div>
                  </div>

                  {/* Content */}
                  <Card className="flex-1 bg-gray-800/50 border-gray-700 hover:border-gray-600 transition-all duration-300">
                    <CardHeader>
                      <div className="flex items-center justify-between mb-4">
                        <div className={`p-3 rounded-lg bg-gradient-to-br ${step.color} text-white`}>
                          {step.icon}
                        </div>
                        <Badge variant="secondary" className="bg-gray-700 text-gray-300">
                          {step.duration}
                        </Badge>
                      </div>
                      <CardTitle className="text-2xl text-white">{step.title}</CardTitle>
                      <CardDescription className="text-gray-300 text-lg">
                        {step.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {step.details.map((detail, idx) => (
                          <li key={idx} className="flex items-center text-gray-400">
                            <CheckCircle className="w-4 h-4 mr-3 text-green-400 flex-shrink-0" />
                            {detail}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>

                  {/* Arrow */}
                  {index < howItWorksSteps.length - 1 && (
                    <div className="hidden lg:block">
                      <ArrowRight className="w-8 h-8 text-gray-600" />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Guarantees Section */}
      <section className="py-8 bg-charcoal">
        <div className="container mx-auto px-6">
          <div className="text-center mb-6">
            <h2 className="text-4xl font-bold mb-3">
              <span className="bg-gradient-to-r from-cyber-gold to-electric-blue bg-clip-text text-transparent">
                Our Guarantees
              </span>
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              We stand behind our process with industry-leading guarantees
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {guarantees.map((guarantee, index) => (
              <Card key={index} className="bg-gray-800/50 border-gray-700 hover:border-cyber-purple/50 transition-all duration-300 group">
                <CardHeader className="text-center p-4">
                  <div className="mx-auto mb-3 p-3 rounded-full bg-gradient-to-br from-cyber-purple/20 to-neon-pink/20 group-hover:from-cyber-purple/30 group-hover:to-neon-pink/30 transition-all duration-300">
                    <div className="text-cyber-purple group-hover:text-neon-pink transition-colors duration-300">
                      {guarantee.icon}
                    </div>
                  </div>
                  <CardTitle className="text-white text-lg">{guarantee.title}</CardTitle>
                  <CardDescription className="text-gray-300">
                    {guarantee.description}
                  </CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 bg-dark-slate">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-4">
            <span className="bg-gradient-to-r from-cyber-purple to-neon-pink bg-clip-text text-transparent">
              Ready to Get Started?
            </span>
          </h2>
          <p className="text-xl text-gray-300 mb-6 max-w-2xl mx-auto">
            Book your free discovery call today and find your perfect South African developer within 7 days.
          </p>
          <Link href="/project-form">
            <Button 
              className="bg-cyber-gradient hover:bg-gradient-to-r hover:from-neon-pink hover:to-cyber-purple text-white font-bold py-4 px-8 rounded-full transition-all duration-300 transform hover:scale-105"
            >
              <Calendar className="mr-2 w-5 h-5" />
              Book Free Discovery Call
            </Button>
          </Link>
        </div>
      </section>

      {/* Project Form Modal */}
      <Dialog open={showProjectForm} onOpenChange={setShowProjectForm}>
        <DialogContent className="bg-charcoal rounded-2xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto border-gray-600" aria-describedby="project-form-description">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-white">Start Your Project</DialogTitle>
            <DialogDescription id="project-form-description" className="text-gray-300">
              Tell us about your project requirements and we'll match you with the perfect developer in 7 days.
            </DialogDescription>
          </DialogHeader>
          
          <ProjectForm onClose={closeProjectForm} />
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <Footer />
    </div>
  );
}